import { type BillableItem, BillableItemsApiFactory, PricesApiFactory } from '@mynt/backoffice-api-spec/billables'
import { type Discount, DiscountsApiFactory } from '@mynt/backoffice-api-spec/discounts'
import { TemplatesApiFactory } from '@mynt/backoffice-api-spec/templates'
import Axios from 'api/axios'
import config from 'config'
import type { CreateDiscount } from './api'
import {
  mapAssignedPriceToBillablePrice,
  mapBillableItemToBillable,
  mapCreateDiscountToCreateDiscountRequest,
  mapDiscountDtoToCustomerDiscount,
  mapTemplate,
} from './mappers'

const billablesApi = BillableItemsApiFactory(undefined, config.boBaseUrl, Axios)
const pricesApi = PricesApiFactory(undefined, config.boBaseUrl, Axios)
const discountApi = DiscountsApiFactory(undefined, config.boBaseUrl, Axios)
const templatesApi = TemplatesApiFactory(undefined, config.boBaseUrl, Axios)

export const getTemplateOnCustomer = (customerId: string) =>
  templatesApi.getTemplateForCustomer(customerId).then(({ data }) => mapTemplate(data))

export const getBillableItemPricesOnCustomer = (customerId: string) =>
  pricesApi.getAllBillableItemPricesOnACustomer(customerId).then(({ data }) => data.map(mapAssignedPriceToBillablePrice))

export const getBillableItemPricesOnTemplate = (templateId: string) =>
  pricesApi.getAllBillableItemPricesOnATemplate(templateId).then(({ data }) => data.map(mapAssignedPriceToBillablePrice))

export const getAllBillables = () => billablesApi.getAllItems().then(({ data }) => data.map(mapBillableItemToBillable))

export const deleteCustomerDiscount = (customerId: string, billableId: string) => discountApi.deleteDiscount(customerId, billableId)

export const getCustomerBillableDiscounts = async (customerId: string) => {
  const discountsPromise = discountApi.getCustomerDiscounts(customerId).then(({ data }) => data)
  const billablesPromise = billablesApi.getAllItems().then(({ data }) => data)

  const [discounts, billables] = await Promise.all([discountsPromise, billablesPromise])

  const data = discounts.reduce<{ discount: Discount; billable: BillableItem }[]>((acc, discount) => {
    if (!discount.billableId) return acc

    const billable = billables.find((b) => b.id === discount.billableId)

    if (!billable) return acc

    return [...acc, { discount, billable }]
  }, [])

  return data.map(({ billable, discount }) => mapDiscountDtoToCustomerDiscount(billable, discount))
}

export const createCustomerBillableDiscount = (customerId: string, discount: CreateDiscount) => {
  return discountApi.createBillableDiscount(customerId, discount.billableId, mapCreateDiscountToCreateDiscountRequest(discount))
}
